import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Icon } from "@/components/ui/icon";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { extractSocialLinks, extractHeadline, ProfileData } from "@/lib/seo";

interface GlobalSettings {
  id?: string;
  site_name: string;
  default_title_pattern: string;
  default_meta_description: string;
  default_og_image_url: string;
  default_twitter_card: string;
  person_name: string;
  person_headline: string;
  person_same_as: string[];
  website_url: string;
}

const defaultSettings: GlobalSettings = {
  site_name: '',
  default_title_pattern: '{{pageTitle}} | {{siteName}}',
  default_meta_description: '',
  default_og_image_url: '',
  default_twitter_card: 'summary_large_image',
  person_name: '',
  person_headline: '',
  person_same_as: [],
  website_url: '',
};

export const SeoGlobalSettingsTab = () => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<GlobalSettings>(defaultSettings);
  const [socialLinks, setSocialLinks] = useState('');

  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ['seo-global-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('seo_global_settings')
        .select('*')
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['profile-for-seo'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profile')
        .select('full_name, summary, website_url, profile_photo_url, linkedin_url, github_url, twitter_url, instagram_url, youtube_url, facebook_url, tiktok_url, medium_url, behance_url, dribbble_url')
        .maybeSingle();
      if (error) throw error;
      return data as ProfileData | null;
    },
  });

  const isLoading = settingsLoading || profileLoading;

  // Derived values from profile
  const profileName = profile?.full_name || '';
  const profileHeadline = extractHeadline(profile?.summary);
  const profileWebsite = profile?.website_url || '';
  const profileSocialLinks = extractSocialLinks(profile);
  const profileOgImage = profile?.profile_photo_url || '';

  useEffect(() => {
    if (settings) {
      const sameAs = Array.isArray(settings.person_same_as) ? settings.person_same_as as string[] : [];
      setFormData({
        id: settings.id,
        site_name: settings.site_name || '',
        default_title_pattern: settings.default_title_pattern || '{{pageTitle}} | {{siteName}}',
        default_meta_description: settings.default_meta_description || '',
        default_og_image_url: settings.default_og_image_url || '',
        default_twitter_card: settings.default_twitter_card || 'summary_large_image',
        person_name: settings.person_name || '',
        person_headline: settings.person_headline || '',
        person_same_as: sameAs,
        website_url: settings.website_url || '',
      });
      setSocialLinks(sameAs.join('\n'));
    }
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: async (data: GlobalSettings) => {
      const payload = {
        site_name: data.site_name,
        default_title_pattern: data.default_title_pattern,
        default_meta_description: data.default_meta_description || null,
        default_og_image_url: data.default_og_image_url || null,
        default_twitter_card: data.default_twitter_card,
        person_name: data.person_name || null,
        person_headline: data.person_headline || null,
        person_same_as: data.person_same_as.length > 0 ? data.person_same_as : null,
        website_url: data.website_url || null,
      };

      if (data.id) {
        const { error } = await supabase
          .from('seo_global_settings')
          .update(payload)
          .eq('id', data.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('seo_global_settings')
          .insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seo-global-settings'] });
      toast.success('Global SEO settings saved');
    },
    onError: (error: Error) => {
      toast.error(`Failed to save: ${error.message}`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsedLinks = socialLinks
      .split('\n')
      .map(link => link.trim())
      .filter(link => link.length > 0);
    
    saveMutation.mutate({
      ...formData,
      person_same_as: parsedLinks,
    });
  };

  const InheritedBadge = ({ value, label }: { value: string; label: string }) => (
    <Badge variant="secondary" className="ml-2 text-xs font-normal bg-primary/10 text-primary">
      <Icon name="link" size={12} className="mr-1" />
      From {label}: {value.length > 30 ? value.substring(0, 30) + '...' : value}
    </Badge>
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent className="space-y-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Profile Integration Notice */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Icon name="info" size={20} className="text-primary mt-0.5" />
            <div>
              <p className="font-medium text-foreground">Auto-populated from Profile</p>
              <p className="text-sm text-muted-foreground">
                Person schema fields (name, headline, website, social links) and default OG image will automatically 
                use data from your Profile if left empty here. Override them below if you want different SEO values.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Basic Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Basic Settings</CardTitle>
          <CardDescription>Configure your site-wide SEO defaults</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="site_name">Site Name</Label>
              <Input
                id="site_name"
                value={formData.site_name}
                onChange={(e) => setFormData({ ...formData, site_name: e.target.value })}
                placeholder="My Portfolio"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center">
                <Label htmlFor="website_url">Website URL</Label>
                {!formData.website_url && profileWebsite && (
                  <InheritedBadge value={profileWebsite} label="Profile" />
                )}
              </div>
              <Input
                id="website_url"
                type="url"
                value={formData.website_url}
                onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                placeholder={profileWebsite || "https://example.com"}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="default_title_pattern">Default Title Pattern</Label>
            <Input
              id="default_title_pattern"
              value={formData.default_title_pattern}
              onChange={(e) => setFormData({ ...formData, default_title_pattern: e.target.value })}
              placeholder="{{pageTitle}} | {{siteName}}"
            />
            <p className="text-xs text-muted-foreground">
              Variables: {"{{pageTitle}}"}, {"{{siteName}}"}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="default_meta_description">Default Meta Description</Label>
            <Textarea
              id="default_meta_description"
              value={formData.default_meta_description}
              onChange={(e) => setFormData({ ...formData, default_meta_description: e.target.value })}
              placeholder="A brief description of your site..."
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              {formData.default_meta_description.length}/160 characters
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Social/OG Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Social Sharing Defaults</CardTitle>
          <CardDescription>Configure OpenGraph and Twitter card defaults</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center">
              <Label htmlFor="default_og_image_url">Default OG Image URL</Label>
              {!formData.default_og_image_url && profileOgImage && (
                <InheritedBadge value="Profile Photo" label="Profile" />
              )}
            </div>
            <Input
              id="default_og_image_url"
              type="url"
              value={formData.default_og_image_url}
              onChange={(e) => setFormData({ ...formData, default_og_image_url: e.target.value })}
              placeholder={profileOgImage || "https://example.com/og-image.jpg"}
            />
            <p className="text-xs text-muted-foreground">
              Recommended size: 1200x630 pixels. Leave empty to use profile photo.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="default_twitter_card">Default Twitter Card Type</Label>
            <Select
              value={formData.default_twitter_card}
              onValueChange={(value) => setFormData({ ...formData, default_twitter_card: value })}
            >
              <SelectTrigger id="default_twitter_card">
                <SelectValue placeholder="Select card type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="summary">Summary</SelectItem>
                <SelectItem value="summary_large_image">Summary with Large Image</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Person Schema Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Structured Data - Person</CardTitle>
          <CardDescription>
            These fields override your Profile data for SEO. Leave empty to use Profile values.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center">
                <Label htmlFor="person_name">Name Override</Label>
                {!formData.person_name && profileName && (
                  <InheritedBadge value={profileName} label="Profile" />
                )}
              </div>
              <Input
                id="person_name"
                value={formData.person_name}
                onChange={(e) => setFormData({ ...formData, person_name: e.target.value })}
                placeholder={profileName || "John Doe"}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center">
                <Label htmlFor="person_headline">Headline Override</Label>
                {!formData.person_headline && profileHeadline && (
                  <InheritedBadge value={profileHeadline} label="Summary" />
                )}
              </div>
              <Input
                id="person_headline"
                value={formData.person_headline}
                onChange={(e) => setFormData({ ...formData, person_headline: e.target.value })}
                placeholder={profileHeadline || "Full Stack Developer"}
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center">
              <Label htmlFor="person_same_as">Social Links Override (one per line)</Label>
              {socialLinks.trim().length === 0 && profileSocialLinks.length > 0 && (
                <Badge variant="secondary" className="ml-2 text-xs font-normal bg-primary/10 text-primary">
                  <Icon name="link" size={12} className="mr-1" />
                  {profileSocialLinks.length} links from Profile
                </Badge>
              )}
            </div>
            <Textarea
              id="person_same_as"
              value={socialLinks}
              onChange={(e) => setSocialLinks(e.target.value)}
              placeholder={profileSocialLinks.length > 0 ? profileSocialLinks.join('\n') : "https://linkedin.com/in/username\nhttps://github.com/username"}
              rows={4}
            />
            <p className="text-xs text-muted-foreground">
              Leave empty to auto-import all social links from your Profile.
            </p>
          </div>
        </CardContent>
      </Card>

      <Button type="submit" disabled={saveMutation.isPending} className="w-full md:w-auto">
        <Icon name="save" size={16} className="mr-2" />
        {saveMutation.isPending ? 'Saving...' : 'Save Global Settings'}
      </Button>
    </form>
  );
};

export default SeoGlobalSettingsTab;
